import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GenerateAudioStreamedOptions,
  GenerateContentByPromptObjectAsyncOptions,
  GenerateContentByPromptObjectOptions,
  GenerateContentByPromptObjectResponse,
  GenerateTextByPromptObjectStreamedOptions,
  GeneratedAudioChunk,
  GeneratedTextChunk,
  PromptProxyCompletedEnvelope,
  generateAudioStreamed as universalGenerateAudioStreamed,
  generateContentByPromptObject as universalGenerateContentByPromptObject,
  generateContentByPromptObjectAsync as universalGenerateContentByPromptObjectAsync,
  generateTextByPromptObjectStreamed as universalGenerateTextByPromptObjectStreamed,
} from './api-infra-v1-prompt-proxy-prompts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ai-gateway' };

/** @internal */
export function generateContentByPromptObject(
  httpClient: HttpClient
): GenerateContentByPromptObjectSignature {
  return (options?: GenerateContentByPromptObjectOptions) =>
    universalGenerateContentByPromptObject(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateContentByPromptObjectSignature {
  /**
   * Generate different content such as text, image, and video according to Prompt object configuration
   * Prompt object is used for all generate content request configuration, such as what vendor, what model and what parameters to use.
   */
  (options?: GenerateContentByPromptObjectOptions): Promise<
    NonNullablePaths<
      GenerateContentByPromptObjectResponse,
      | `response.openAiChatCompletionResponse.model`
      | `response.openAiChatCompletionResponse.choices`
      | `response.openAiChatCompletionResponse.choices.${number}.message.role`
      | `response.azureChatCompletionResponse.model`
      | `response.azureChatCompletionResponse.choices`
      | `response.azureChatCompletionResponse.choices.${number}.message.role`
      | `response.googleGeminiGenerateContentResponse.candidates`
      | `response.googleGeminiGenerateContentResponse.candidates.${number}.finishReason`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.promptTokensDetails`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.promptTokensDetails.${number}.modality`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.candidatesTokensDetails`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.cacheTokensDetails`
      | `response.anthropicClaudeResponse.responseId`
      | `response.anthropicClaudeResponse.model`
      | `response.anthropicClaudeResponse.responseType`
      | `response.anthropicClaudeResponse.role`
      | `response.anthropicClaudeResponse.content`
      | `response.anthropicClaudeResponse.usage.inputTokens`
      | `response.anthropicClaudeResponse.usage.outputTokens`
      | `response.anthropicClaudeResponse.contentBlocks`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.text`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.textContent.text`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.textContent.cacheControl.type`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.imageUrl.mediaType`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.thinking.signature`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.thinking.thinking`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.redactedThinking.data`
      | `response.googleAnthropicClaudeResponse.responseId`
      | `response.googleAnthropicClaudeResponse.model`
      | `response.googleAnthropicClaudeResponse.responseType`
      | `response.googleAnthropicClaudeResponse.role`
      | `response.googleAnthropicClaudeResponse.content`
      | `response.googleAnthropicClaudeResponse.usage.inputTokens`
      | `response.googleAnthropicClaudeResponse.usage.outputTokens`
      | `response.googleAnthropicClaudeResponse.contentBlocks`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.text`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.textContent.text`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.textContent.cacheControl.type`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.imageUrl.mediaType`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.thinking.signature`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.thinking.thinking`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.redactedThinking.data`
      | `response.invokeAnthropicModelResponse.responseId`
      | `response.invokeAnthropicModelResponse.model`
      | `response.invokeAnthropicModelResponse.type`
      | `response.invokeAnthropicModelResponse.role`
      | `response.invokeAnthropicModelResponse.usage.cacheCreation.ephemeral1hInputTokens`
      | `response.invokeAnthropicModelResponse.usage.cacheCreation.ephemeral5mInputTokens`
      | `response.invokeAnthropicModelResponse.usage.inputTokens`
      | `response.invokeAnthropicModelResponse.usage.outputTokens`
      | `response.invokeAnthropicModelResponse.usage.serverToolUse.webSearchRequests`
      | `response.invokeAnthropicModelResponse.usage.serverToolUse.webFetchRequests`
      | `response.invokeAnthropicModelResponse.container.expiresAt`
      | `response.invokeAnthropicModelResponse.container._id`
      | `response.invokeAnthropicModelResponse.content`
      | `response.invokeAnthropicModelResponse.content.${number}.textContent.text`
      | `response.invokeAnthropicModelResponse.content.${number}.textContent.cacheControl.type`
      | `response.invokeAnthropicModelResponse.content.${number}.image.mediaType`
      | `response.invokeAnthropicModelResponse.content.${number}.thinking.signature`
      | `response.invokeAnthropicModelResponse.content.${number}.thinking.thinking`
      | `response.invokeAnthropicModelResponse.content.${number}.redactedThinking.data`
      | `response.invokeAnthropicModelResponse.content.${number}.document.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.contentSuccess.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.contentError.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.type`
      | `response.amazonConverseResponse.output.message.role`
      | `response.amazonConverseResponse.output.message.content`
      | `response.amazonConverseResponse.output.message.content.${number}.text`
      | `response.amazonConverseResponse.output.message.content.${number}.reasoningContent.reasoningText.text`
      | `response.amazonConverseResponse.output.message.content.${number}.toolUse.toolUseId`
      | `response.amazonConverseResponse.output.message.content.${number}.toolUse.name`
      | `response.amazonConverseResponse.output.message.content.${number}.toolResult.toolUseId`
      | `response.amazonConverseResponse.usage.inputTokens`
      | `response.amazonConverseResponse.usage.outputTokens`
      | `response.amazonConverseResponse.usage.totalTokens`
      | `response.amazonConverseResponse.metrics.latencyMs`
      | `response.perplexityChatCompletionResponse.model`
      | `response.perplexityChatCompletionResponse.citations`
      | `response.perplexityChatCompletionResponse.choices`
      | `response.perplexityChatCompletionResponse.choices.${number}.message.content`
      | `response.perplexityChatCompletionResponse.choices.${number}.message.role`
      | `response.perplexityChatCompletionResponse.images`
      | `response.perplexityChatCompletionResponse.relatedQuestions`
      | `response.openAiCreateImageResponse.data`
      | `response.openAiCreateImageResponse.model`
      | `response.stabilityAiTextToImageResponse.data`
      | `response.stabilityAiTextToImageResponse.model`
      | `response.stabilityAiGenerateCoreResponse.data`
      | `response.stabilityAiGenerateCoreResponse.model`
      | `response.stabilityAiStableDiffusionResponse.data`
      | `response.stabilityAiStableDiffusionResponse.model`
      | `response.replicateCreatePredictionResponse.output`
      | `response.replicateCreatePredictionResponse.textOutput`
      | `response.stabilityAiEditImageWithPromptResponse.data`
      | `response.stabilityAiEditImageWithPromptResponse.model`
      | `response.runwareTextToImageResponse.data`
      | `response.runwareTextToImageResponse.data.${number}.taskUuid`
      | `response.runwareTextToImageResponse.data.${number}.imageUuid`
      | `response.runwareTextToImageResponse.data.${number}.nsfwContent`
      | `response.googleGenerateImageResponse.predictions`
      | `response.googleGenerateVideoResponse.videos`
      | `response.mlPlatformGenerateImageResponse.output`
      | `response.openAiCreateOpenAiImageResponse.data`
      | `response.openAiCreateOpenAiImageResponse.model`
      | `response.openAiEditOpenAiImageResponse.data`
      | `response.openAiEditOpenAiImageResponse.model`
      | `response.googleCreateChatCompletionResponse.model`
      | `response.googleCreateChatCompletionResponse.choices`
      | `response.googleCreateChatCompletionResponse.choices.${number}.message.role`
      | `response.mlPlatformOpenAiRawResponse.modelId`
      | `response.mlPlatformOpenAiRawResponse.choices`
      | `response.mlPlatformOpenAiRawResponse.choices.${number}.message.role`
      | `response.runwareVideoInferenceResponse.data`
      | `response.runwareVideoInferenceResponse.data.${number}.taskType`
      | `response.runwareVideoInferenceResponse.data.${number}.taskUuid`
      | `response.openAiResponsesResponse.model`
      | `response.openAiResponsesResponse.output`
      | `response.azureOpenAiResponsesResponse.model`
      | `response.azureOpenAiResponsesResponse.output`
      | `response.fireworksInvokeCustomOpenAiResponse.modelId`
      | `response.fireworksInvokeCustomOpenAiResponse.choices`
      | `response.fireworksInvokeCustomOpenAiResponse.choices.${number}.message.role`
      | `response.bytedanceGenerateImageResponse.data`
      | `response.generatedContent.texts`
      | `response.generatedContent.images`
      | `response.generatedContent.images.${number}.url`
      | `response.generatedContent.videos`
      | `response.generatedContent.thinkingTexts`
      | `response.generatedContent.tools`
      | `response.generatedContent.tools.${number}.name`
      | `materializedPrompt.openAiChatCompletionRequest.model`
      | `materializedPrompt.openAiChatCompletionRequest.messages`
      | `materializedPrompt.openAiChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.openAiChatCompletionRequest.functions`
      | `materializedPrompt.openAiChatCompletionRequest.stop`
      | `materializedPrompt.openAiChatCompletionRequest.tools`
      | `materializedPrompt.openAiChatCompletionRequest.tools.${number}.type`
      | `materializedPrompt.azureChatCompletionRequest.model`
      | `materializedPrompt.azureChatCompletionRequest.messages`
      | `materializedPrompt.azureChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.azureChatCompletionRequest.functions`
      | `materializedPrompt.azureChatCompletionRequest.stop`
      | `materializedPrompt.azureChatCompletionRequest.tools`
      | `materializedPrompt.azureChatCompletionRequest.tools.${number}.type`
      | `materializedPrompt.googleGeminiGenerateContentRequest.model`
      | `materializedPrompt.googleGeminiGenerateContentRequest.contents`
      | `materializedPrompt.googleGeminiGenerateContentRequest.contents.${number}.role`
      | `materializedPrompt.googleGeminiGenerateContentRequest.systemInstruction.parts`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools.${number}.googleSearchRetrieval.dynamicRetrievalConfig.mode`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools.${number}.computerUse.environment`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings.${number}.category`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings.${number}.threshold`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.stopSequences`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.responseModalities`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.imageConfig.personGeneration`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.mediaResolution`
      | `materializedPrompt.googleGeminiGenerateContentRequest.toolConfig.functionCallingConfig.mode`
      | `materializedPrompt.googleGeminiGenerateContentRequest.toolConfig.functionCallingConfig.allowedFunctionNames`
      | `materializedPrompt.anthropicClaudeRequest.model`
      | `materializedPrompt.anthropicClaudeRequest.messages`
      | `materializedPrompt.anthropicClaudeRequest.messages.${number}.role`
      | `materializedPrompt.anthropicClaudeRequest.systemPrompt`
      | `materializedPrompt.anthropicClaudeRequest.stopSequences`
      | `materializedPrompt.anthropicClaudeRequest.tools`
      | `materializedPrompt.anthropicClaudeRequest.tools.${number}.cacheControl.type`
      | `materializedPrompt.anthropicClaudeRequest.toolChoice.type`
      | `materializedPrompt.anthropicClaudeRequest.thinking.budgetTokens`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers.${number}.name`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers.${number}.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.model`
      | `materializedPrompt.googleAnthropicClaudeRequest.messages`
      | `materializedPrompt.googleAnthropicClaudeRequest.messages.${number}.role`
      | `materializedPrompt.googleAnthropicClaudeRequest.systemPrompt`
      | `materializedPrompt.googleAnthropicClaudeRequest.stopSequences`
      | `materializedPrompt.googleAnthropicClaudeRequest.tools`
      | `materializedPrompt.googleAnthropicClaudeRequest.tools.${number}.cacheControl.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.toolChoice.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.thinking.budgetTokens`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers.${number}.name`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers.${number}.type`
      | `materializedPrompt.invokeAnthropicModelRequest.model`
      | `materializedPrompt.invokeAnthropicModelRequest.messages`
      | `materializedPrompt.invokeAnthropicModelRequest.messages.${number}.role`
      | `materializedPrompt.invokeAnthropicModelRequest.systemPrompt`
      | `materializedPrompt.invokeAnthropicModelRequest.stopSequences`
      | `materializedPrompt.invokeAnthropicModelRequest.tools`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.custom.name`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.custom.cacheControl.type`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.computerUse.displayWidthPx`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.computerUse.displayHeightPx`
      | `materializedPrompt.invokeAnthropicModelRequest.toolChoice.type`
      | `materializedPrompt.invokeAnthropicModelRequest.thinking.budgetTokens`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers.${number}.name`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers.${number}.type`
      | `materializedPrompt.llamaModelRequest.model`
      | `materializedPrompt.amazonConverseRequest.model`
      | `materializedPrompt.amazonConverseRequest.messages`
      | `materializedPrompt.amazonConverseRequest.messages.${number}.role`
      | `materializedPrompt.amazonConverseRequest.inferenceConfig.stopSequences`
      | `materializedPrompt.amazonConverseRequest.toolConfig.tools`
      | `materializedPrompt.amazonConverseRequest.toolConfig.tools.${number}.toolSpec.name`
      | `materializedPrompt.amazonConverseRequest.system`
      | `materializedPrompt.amazonConverseRequest.additionalModelResponseFieldPaths`
      | `materializedPrompt.openAiCreateImageRequest.model`
      | `materializedPrompt.openAiCreateImageRequest.quality`
      | `materializedPrompt.openAiCreateImageRequest.size`
      | `materializedPrompt.openAiCreateImageRequest.style`
      | `materializedPrompt.stabilityAiTextToImageRequest.model`
      | `materializedPrompt.stabilityAiTextToImageRequest.textPrompts`
      | `materializedPrompt.stabilityAiTextToImageRequest.clipGuidancePreset`
      | `materializedPrompt.stabilityAiTextToImageRequest.sampler`
      | `materializedPrompt.stabilityAiTextToImageRequest.stylePreset`
      | `materializedPrompt.stabilityAiGenerateCoreRequest.model`
      | `materializedPrompt.stabilityAiGenerateCoreRequest.stylePreset`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.mode`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.model`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.outputFormat`
      | `materializedPrompt.blackForestLabsGenerateImageRequest.model`
      | `materializedPrompt.replicateCreatePredictionRequest.lucatacoFlorence2Large.taskInput`
      | `materializedPrompt.replicateCreatePredictionRequest.perceptronIsaac01.response`
      | `materializedPrompt.replicateCreatePredictionRequest.qwenImageLayered.image`
      | `materializedPrompt.replicateCreatePredictionRequest.model`
      | `materializedPrompt.stabilityAiEditWithPromptRequest.model`
      | `materializedPrompt.stabilityAiEditWithPromptRequest.stylePreset`
      | `materializedPrompt.runwareTextToImageRequest.positivePrompt`
      | `materializedPrompt.runwareTextToImageRequest.height`
      | `materializedPrompt.runwareTextToImageRequest.width`
      | `materializedPrompt.runwareTextToImageRequest.referenceImages`
      | `materializedPrompt.runwareTextToImageRequest.model`
      | `materializedPrompt.runwareTextToImageRequest.loraModels`
      | `materializedPrompt.runwareTextToImageRequest.inputs.referenceImages`
      | `materializedPrompt.mlPlatformLlamaModelRequest.modelId`
      | `materializedPrompt.perplexityChatCompletionRequest.model`
      | `materializedPrompt.perplexityChatCompletionRequest.messages`
      | `materializedPrompt.perplexityChatCompletionRequest.messages.${number}.content`
      | `materializedPrompt.perplexityChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.perplexityChatCompletionRequest.searchDomainFilter`
      | `materializedPrompt.perplexityChatCompletionRequest.responseFormat.jsonSchema`
      | `materializedPrompt.perplexityChatCompletionRequest.responseFormat.regex`
      | `materializedPrompt.googleGenerateImageRequest.model`
      | `materializedPrompt.googleGenerateImageRequest.instances`
      | `materializedPrompt.mlPlatformGenerateImageRequest.model`
      | `materializedPrompt.openAiCreateOpenAiImageRequest.model`
      | `materializedPrompt.openAiEditOpenAiImageRequest.model`
      | `materializedPrompt.openAiEditOpenAiImageRequest.imageUrls`
      | `materializedPrompt.googleGenerateVideoRequest.model`
      | `materializedPrompt.googleGenerateVideoRequest.instances`
      | `materializedPrompt.googleCreateChatCompletionRequest.model`
      | `materializedPrompt.googleCreateChatCompletionRequest.messages`
      | `materializedPrompt.googleCreateChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.modelId`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.messages`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.messages.${number}.role`
      | `materializedPrompt.runwareVideoInferenceRequest.outputFormat`
      | `materializedPrompt.runwareVideoInferenceRequest.frameImages`
      | `materializedPrompt.runwareVideoInferenceRequest.frameImages.${number}.inputImage`
      | `materializedPrompt.runwareVideoInferenceRequest.referenceImages`
      | `materializedPrompt.runwareVideoInferenceRequest.model`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.frameImages`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.referenceImages`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.referenceVideos`
      | `materializedPrompt.openAiResponsesRequest.model`
      | `materializedPrompt.openAiResponsesRequest.include`
      | `materializedPrompt.openAiResponsesRequest.input`
      | `materializedPrompt.openAiResponsesRequest.input.${number}.message.role`
      | `materializedPrompt.openAiResponsesRequest.tools`
      | `materializedPrompt.azureOpenAiResponsesRequest.model`
      | `materializedPrompt.azureOpenAiResponsesRequest.include`
      | `materializedPrompt.azureOpenAiResponsesRequest.input`
      | `materializedPrompt.azureOpenAiResponsesRequest.input.${number}.message.role`
      | `materializedPrompt.azureOpenAiResponsesRequest.tools`
      | `materializedPrompt.openAiCreateVideoRequest.prompt`
      | `materializedPrompt.openAiCreateVideoRequest.model`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.modelId`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.messages`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.messages.${number}.role`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.functions`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.stop`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.tools`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.tools.${number}.type`
      | `materializedPrompt.bytedanceCreateContentTaskRequest.model`
      | `materializedPrompt.bytedanceCreateContentTaskRequest.content`
      | `materializedPrompt.bytedanceGenerateImageRequest.model`
      | `materializedPrompt.bytedanceGenerateImageRequest.prompt`
      | `materializedPrompt.bytedanceGenerateImageRequest.image`
      | `materializedPrompt.templatedParameterNames`
      | `materializedPrompt.templatedDynamicPropertiesNames`
      | `predictionId`,
      10
    >
  >;
}

/** @internal */
export function generateTextByPromptObjectStreamed(
  httpClient: HttpClient
): GenerateTextByPromptObjectStreamedSignature {
  return (options?: GenerateTextByPromptObjectStreamedOptions) =>
    universalGenerateTextByPromptObjectStreamed(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateTextByPromptObjectStreamedSignature {
  /**
   * Generate text according to Prompt object configuration.
   * Prompt object is used for all generate text request configuration, such as what vendor, what model and what parameters to use.
   * The response is streamed back in chunks.
   */
  (options?: GenerateTextByPromptObjectStreamedOptions): Promise<
    NonNullablePaths<
      GeneratedTextChunk,
      | `azureChatCompletionChunk.choices`
      | `azureChatCompletionChunk.choices.${number}.delta.role`
      | `azureChatCompletionChunk.model`
      | `openaiChatCompletionChunk.choices`
      | `openaiChatCompletionChunk.choices.${number}.delta.role`
      | `openaiChatCompletionChunk.model`
      | `googleAnthropicStreamChunk.toolUse.cacheControl.type`
      | `googleAnthropicStreamChunk.contentBlockDelta.text`
      | `googleAnthropicStreamChunk.contentBlockDelta.partialJson`
      | `googleAnthropicStreamChunk.contentBlockDelta.thinking`
      | `googleAnthropicStreamChunk.contentBlockDelta.signature`
      | `googleAnthropicStreamChunk.messageDelta.usage.inputTokens`
      | `googleAnthropicStreamChunk.messageDelta.usage.outputTokens`
      | `googleAnthropicStreamChunk.redactedThinking.data`
      | `googleAnthropicStreamChunk.responseId`
      | `googleAnthropicStreamChunk.model`
      | `googleGeminiStreamChunk.candidates`
      | `googleGeminiStreamChunk.candidates.${number}.finishReason`
      | `googleGeminiStreamChunk.usageMetadata.promptTokensDetails`
      | `googleGeminiStreamChunk.usageMetadata.promptTokensDetails.${number}.modality`
      | `googleGeminiStreamChunk.usageMetadata.candidatesTokensDetails`
      | `googleGeminiStreamChunk.usageMetadata.cacheTokensDetails`
      | `amazonAnthropicStreamChunk.toolUse.cacheControl.type`
      | `amazonAnthropicStreamChunk.contentBlockDelta.text`
      | `amazonAnthropicStreamChunk.contentBlockDelta.partialJson`
      | `amazonAnthropicStreamChunk.contentBlockDelta.thinking`
      | `amazonAnthropicStreamChunk.contentBlockDelta.signature`
      | `amazonAnthropicStreamChunk.messageDelta.usage.inputTokens`
      | `amazonAnthropicStreamChunk.messageDelta.usage.outputTokens`
      | `amazonAnthropicStreamChunk.redactedThinking.data`
      | `amazonAnthropicStreamChunk.responseId`
      | `amazonAnthropicStreamChunk.model`
      | `anthropicStreamChunk.toolUse.cacheControl.type`
      | `anthropicStreamChunk.webSearchToolResult.contentResults.items`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.type`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.type`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content.${number}.textContent.text`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content.${number}.image.mediaType`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content.${number}.thinking.signature`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content.${number}.thinking.thinking`
      | `anthropicStreamChunk.webFetchToolResult.contentSuccess.content.source.content.${number}.redactedThinking.data`
      | `anthropicStreamChunk.webFetchToolResult.contentError.type`
      | `anthropicStreamChunk.webFetchToolResult.type`
      | `anthropicStreamChunk.contentBlockDelta.text`
      | `anthropicStreamChunk.contentBlockDelta.partialJson`
      | `anthropicStreamChunk.contentBlockDelta.thinking`
      | `anthropicStreamChunk.contentBlockDelta.signature`
      | `anthropicStreamChunk.messageDelta.usage.cacheCreation.ephemeral1hInputTokens`
      | `anthropicStreamChunk.messageDelta.usage.cacheCreation.ephemeral5mInputTokens`
      | `anthropicStreamChunk.messageDelta.usage.inputTokens`
      | `anthropicStreamChunk.messageDelta.usage.outputTokens`
      | `anthropicStreamChunk.messageDelta.usage.serverToolUse.webSearchRequests`
      | `anthropicStreamChunk.messageDelta.usage.serverToolUse.webFetchRequests`
      | `anthropicStreamChunk.responseId`
      | `anthropicStreamChunk.model`
      | `fireworksChatCompletionChunk.choices`
      | `fireworksChatCompletionChunk.choices.${number}.delta.role`
      | `fireworksChatCompletionChunk.modelId`
      | `predictionId`,
      10
    >
  >;
}

/** @internal */
export function generateContentByPromptObjectAsync(
  httpClient: HttpClient
): GenerateContentByPromptObjectAsyncSignature {
  return (options?: GenerateContentByPromptObjectAsyncOptions) =>
    universalGenerateContentByPromptObjectAsync(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateContentByPromptObjectAsyncSignature {
  /** */
  (options?: GenerateContentByPromptObjectAsyncOptions): Promise<
    NonNullablePaths<
      GenerateContentByPromptObjectResponse,
      | `response.openAiChatCompletionResponse.model`
      | `response.openAiChatCompletionResponse.choices`
      | `response.openAiChatCompletionResponse.choices.${number}.message.role`
      | `response.azureChatCompletionResponse.model`
      | `response.azureChatCompletionResponse.choices`
      | `response.azureChatCompletionResponse.choices.${number}.message.role`
      | `response.googleGeminiGenerateContentResponse.candidates`
      | `response.googleGeminiGenerateContentResponse.candidates.${number}.finishReason`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.promptTokensDetails`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.promptTokensDetails.${number}.modality`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.candidatesTokensDetails`
      | `response.googleGeminiGenerateContentResponse.usageMetadata.cacheTokensDetails`
      | `response.anthropicClaudeResponse.responseId`
      | `response.anthropicClaudeResponse.model`
      | `response.anthropicClaudeResponse.responseType`
      | `response.anthropicClaudeResponse.role`
      | `response.anthropicClaudeResponse.content`
      | `response.anthropicClaudeResponse.usage.inputTokens`
      | `response.anthropicClaudeResponse.usage.outputTokens`
      | `response.anthropicClaudeResponse.contentBlocks`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.text`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.textContent.text`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.textContent.cacheControl.type`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.imageUrl.mediaType`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.thinking.signature`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.thinking.thinking`
      | `response.anthropicClaudeResponse.contentBlocks.${number}.redactedThinking.data`
      | `response.googleAnthropicClaudeResponse.responseId`
      | `response.googleAnthropicClaudeResponse.model`
      | `response.googleAnthropicClaudeResponse.responseType`
      | `response.googleAnthropicClaudeResponse.role`
      | `response.googleAnthropicClaudeResponse.content`
      | `response.googleAnthropicClaudeResponse.usage.inputTokens`
      | `response.googleAnthropicClaudeResponse.usage.outputTokens`
      | `response.googleAnthropicClaudeResponse.contentBlocks`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.text`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.textContent.text`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.textContent.cacheControl.type`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.imageUrl.mediaType`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.thinking.signature`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.thinking.thinking`
      | `response.googleAnthropicClaudeResponse.contentBlocks.${number}.redactedThinking.data`
      | `response.invokeAnthropicModelResponse.responseId`
      | `response.invokeAnthropicModelResponse.model`
      | `response.invokeAnthropicModelResponse.type`
      | `response.invokeAnthropicModelResponse.role`
      | `response.invokeAnthropicModelResponse.usage.cacheCreation.ephemeral1hInputTokens`
      | `response.invokeAnthropicModelResponse.usage.cacheCreation.ephemeral5mInputTokens`
      | `response.invokeAnthropicModelResponse.usage.inputTokens`
      | `response.invokeAnthropicModelResponse.usage.outputTokens`
      | `response.invokeAnthropicModelResponse.usage.serverToolUse.webSearchRequests`
      | `response.invokeAnthropicModelResponse.usage.serverToolUse.webFetchRequests`
      | `response.invokeAnthropicModelResponse.container.expiresAt`
      | `response.invokeAnthropicModelResponse.container._id`
      | `response.invokeAnthropicModelResponse.content`
      | `response.invokeAnthropicModelResponse.content.${number}.textContent.text`
      | `response.invokeAnthropicModelResponse.content.${number}.textContent.cacheControl.type`
      | `response.invokeAnthropicModelResponse.content.${number}.image.mediaType`
      | `response.invokeAnthropicModelResponse.content.${number}.thinking.signature`
      | `response.invokeAnthropicModelResponse.content.${number}.thinking.thinking`
      | `response.invokeAnthropicModelResponse.content.${number}.redactedThinking.data`
      | `response.invokeAnthropicModelResponse.content.${number}.document.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.contentSuccess.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.contentError.type`
      | `response.invokeAnthropicModelResponse.content.${number}.webFetchToolResult.type`
      | `response.amazonConverseResponse.output.message.role`
      | `response.amazonConverseResponse.output.message.content`
      | `response.amazonConverseResponse.output.message.content.${number}.text`
      | `response.amazonConverseResponse.output.message.content.${number}.reasoningContent.reasoningText.text`
      | `response.amazonConverseResponse.output.message.content.${number}.toolUse.toolUseId`
      | `response.amazonConverseResponse.output.message.content.${number}.toolUse.name`
      | `response.amazonConverseResponse.output.message.content.${number}.toolResult.toolUseId`
      | `response.amazonConverseResponse.usage.inputTokens`
      | `response.amazonConverseResponse.usage.outputTokens`
      | `response.amazonConverseResponse.usage.totalTokens`
      | `response.amazonConverseResponse.metrics.latencyMs`
      | `response.perplexityChatCompletionResponse.model`
      | `response.perplexityChatCompletionResponse.citations`
      | `response.perplexityChatCompletionResponse.choices`
      | `response.perplexityChatCompletionResponse.choices.${number}.message.content`
      | `response.perplexityChatCompletionResponse.choices.${number}.message.role`
      | `response.perplexityChatCompletionResponse.images`
      | `response.perplexityChatCompletionResponse.relatedQuestions`
      | `response.openAiCreateImageResponse.data`
      | `response.openAiCreateImageResponse.model`
      | `response.stabilityAiTextToImageResponse.data`
      | `response.stabilityAiTextToImageResponse.model`
      | `response.stabilityAiGenerateCoreResponse.data`
      | `response.stabilityAiGenerateCoreResponse.model`
      | `response.stabilityAiStableDiffusionResponse.data`
      | `response.stabilityAiStableDiffusionResponse.model`
      | `response.replicateCreatePredictionResponse.output`
      | `response.replicateCreatePredictionResponse.textOutput`
      | `response.stabilityAiEditImageWithPromptResponse.data`
      | `response.stabilityAiEditImageWithPromptResponse.model`
      | `response.runwareTextToImageResponse.data`
      | `response.runwareTextToImageResponse.data.${number}.taskUuid`
      | `response.runwareTextToImageResponse.data.${number}.imageUuid`
      | `response.runwareTextToImageResponse.data.${number}.nsfwContent`
      | `response.googleGenerateImageResponse.predictions`
      | `response.googleGenerateVideoResponse.videos`
      | `response.mlPlatformGenerateImageResponse.output`
      | `response.openAiCreateOpenAiImageResponse.data`
      | `response.openAiCreateOpenAiImageResponse.model`
      | `response.openAiEditOpenAiImageResponse.data`
      | `response.openAiEditOpenAiImageResponse.model`
      | `response.googleCreateChatCompletionResponse.model`
      | `response.googleCreateChatCompletionResponse.choices`
      | `response.googleCreateChatCompletionResponse.choices.${number}.message.role`
      | `response.mlPlatformOpenAiRawResponse.modelId`
      | `response.mlPlatformOpenAiRawResponse.choices`
      | `response.mlPlatformOpenAiRawResponse.choices.${number}.message.role`
      | `response.runwareVideoInferenceResponse.data`
      | `response.runwareVideoInferenceResponse.data.${number}.taskType`
      | `response.runwareVideoInferenceResponse.data.${number}.taskUuid`
      | `response.openAiResponsesResponse.model`
      | `response.openAiResponsesResponse.output`
      | `response.azureOpenAiResponsesResponse.model`
      | `response.azureOpenAiResponsesResponse.output`
      | `response.fireworksInvokeCustomOpenAiResponse.modelId`
      | `response.fireworksInvokeCustomOpenAiResponse.choices`
      | `response.fireworksInvokeCustomOpenAiResponse.choices.${number}.message.role`
      | `response.bytedanceGenerateImageResponse.data`
      | `response.generatedContent.texts`
      | `response.generatedContent.images`
      | `response.generatedContent.images.${number}.url`
      | `response.generatedContent.videos`
      | `response.generatedContent.thinkingTexts`
      | `response.generatedContent.tools`
      | `response.generatedContent.tools.${number}.name`
      | `materializedPrompt.openAiChatCompletionRequest.model`
      | `materializedPrompt.openAiChatCompletionRequest.messages`
      | `materializedPrompt.openAiChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.openAiChatCompletionRequest.functions`
      | `materializedPrompt.openAiChatCompletionRequest.stop`
      | `materializedPrompt.openAiChatCompletionRequest.tools`
      | `materializedPrompt.openAiChatCompletionRequest.tools.${number}.type`
      | `materializedPrompt.azureChatCompletionRequest.model`
      | `materializedPrompt.azureChatCompletionRequest.messages`
      | `materializedPrompt.azureChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.azureChatCompletionRequest.functions`
      | `materializedPrompt.azureChatCompletionRequest.stop`
      | `materializedPrompt.azureChatCompletionRequest.tools`
      | `materializedPrompt.azureChatCompletionRequest.tools.${number}.type`
      | `materializedPrompt.googleGeminiGenerateContentRequest.model`
      | `materializedPrompt.googleGeminiGenerateContentRequest.contents`
      | `materializedPrompt.googleGeminiGenerateContentRequest.contents.${number}.role`
      | `materializedPrompt.googleGeminiGenerateContentRequest.systemInstruction.parts`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools.${number}.googleSearchRetrieval.dynamicRetrievalConfig.mode`
      | `materializedPrompt.googleGeminiGenerateContentRequest.tools.${number}.computerUse.environment`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings.${number}.category`
      | `materializedPrompt.googleGeminiGenerateContentRequest.safetySettings.${number}.threshold`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.stopSequences`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.responseModalities`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.imageConfig.personGeneration`
      | `materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.mediaResolution`
      | `materializedPrompt.googleGeminiGenerateContentRequest.toolConfig.functionCallingConfig.mode`
      | `materializedPrompt.googleGeminiGenerateContentRequest.toolConfig.functionCallingConfig.allowedFunctionNames`
      | `materializedPrompt.anthropicClaudeRequest.model`
      | `materializedPrompt.anthropicClaudeRequest.messages`
      | `materializedPrompt.anthropicClaudeRequest.messages.${number}.role`
      | `materializedPrompt.anthropicClaudeRequest.systemPrompt`
      | `materializedPrompt.anthropicClaudeRequest.stopSequences`
      | `materializedPrompt.anthropicClaudeRequest.tools`
      | `materializedPrompt.anthropicClaudeRequest.tools.${number}.cacheControl.type`
      | `materializedPrompt.anthropicClaudeRequest.toolChoice.type`
      | `materializedPrompt.anthropicClaudeRequest.thinking.budgetTokens`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers.${number}.name`
      | `materializedPrompt.anthropicClaudeRequest.mcpServers.${number}.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.model`
      | `materializedPrompt.googleAnthropicClaudeRequest.messages`
      | `materializedPrompt.googleAnthropicClaudeRequest.messages.${number}.role`
      | `materializedPrompt.googleAnthropicClaudeRequest.systemPrompt`
      | `materializedPrompt.googleAnthropicClaudeRequest.stopSequences`
      | `materializedPrompt.googleAnthropicClaudeRequest.tools`
      | `materializedPrompt.googleAnthropicClaudeRequest.tools.${number}.cacheControl.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.toolChoice.type`
      | `materializedPrompt.googleAnthropicClaudeRequest.thinking.budgetTokens`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers.${number}.name`
      | `materializedPrompt.googleAnthropicClaudeRequest.mcpServers.${number}.type`
      | `materializedPrompt.invokeAnthropicModelRequest.model`
      | `materializedPrompt.invokeAnthropicModelRequest.messages`
      | `materializedPrompt.invokeAnthropicModelRequest.messages.${number}.role`
      | `materializedPrompt.invokeAnthropicModelRequest.systemPrompt`
      | `materializedPrompt.invokeAnthropicModelRequest.stopSequences`
      | `materializedPrompt.invokeAnthropicModelRequest.tools`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.custom.name`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.custom.cacheControl.type`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.computerUse.displayWidthPx`
      | `materializedPrompt.invokeAnthropicModelRequest.tools.${number}.computerUse.displayHeightPx`
      | `materializedPrompt.invokeAnthropicModelRequest.toolChoice.type`
      | `materializedPrompt.invokeAnthropicModelRequest.thinking.budgetTokens`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers.${number}.name`
      | `materializedPrompt.invokeAnthropicModelRequest.mcpServers.${number}.type`
      | `materializedPrompt.llamaModelRequest.model`
      | `materializedPrompt.amazonConverseRequest.model`
      | `materializedPrompt.amazonConverseRequest.messages`
      | `materializedPrompt.amazonConverseRequest.messages.${number}.role`
      | `materializedPrompt.amazonConverseRequest.inferenceConfig.stopSequences`
      | `materializedPrompt.amazonConverseRequest.toolConfig.tools`
      | `materializedPrompt.amazonConverseRequest.toolConfig.tools.${number}.toolSpec.name`
      | `materializedPrompt.amazonConverseRequest.system`
      | `materializedPrompt.amazonConverseRequest.additionalModelResponseFieldPaths`
      | `materializedPrompt.openAiCreateImageRequest.model`
      | `materializedPrompt.openAiCreateImageRequest.quality`
      | `materializedPrompt.openAiCreateImageRequest.size`
      | `materializedPrompt.openAiCreateImageRequest.style`
      | `materializedPrompt.stabilityAiTextToImageRequest.model`
      | `materializedPrompt.stabilityAiTextToImageRequest.textPrompts`
      | `materializedPrompt.stabilityAiTextToImageRequest.clipGuidancePreset`
      | `materializedPrompt.stabilityAiTextToImageRequest.sampler`
      | `materializedPrompt.stabilityAiTextToImageRequest.stylePreset`
      | `materializedPrompt.stabilityAiGenerateCoreRequest.model`
      | `materializedPrompt.stabilityAiGenerateCoreRequest.stylePreset`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.mode`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.model`
      | `materializedPrompt.stabilityAiStableDiffusionRequest.outputFormat`
      | `materializedPrompt.blackForestLabsGenerateImageRequest.model`
      | `materializedPrompt.replicateCreatePredictionRequest.lucatacoFlorence2Large.taskInput`
      | `materializedPrompt.replicateCreatePredictionRequest.perceptronIsaac01.response`
      | `materializedPrompt.replicateCreatePredictionRequest.qwenImageLayered.image`
      | `materializedPrompt.replicateCreatePredictionRequest.model`
      | `materializedPrompt.stabilityAiEditWithPromptRequest.model`
      | `materializedPrompt.stabilityAiEditWithPromptRequest.stylePreset`
      | `materializedPrompt.runwareTextToImageRequest.positivePrompt`
      | `materializedPrompt.runwareTextToImageRequest.height`
      | `materializedPrompt.runwareTextToImageRequest.width`
      | `materializedPrompt.runwareTextToImageRequest.referenceImages`
      | `materializedPrompt.runwareTextToImageRequest.model`
      | `materializedPrompt.runwareTextToImageRequest.loraModels`
      | `materializedPrompt.runwareTextToImageRequest.inputs.referenceImages`
      | `materializedPrompt.mlPlatformLlamaModelRequest.modelId`
      | `materializedPrompt.perplexityChatCompletionRequest.model`
      | `materializedPrompt.perplexityChatCompletionRequest.messages`
      | `materializedPrompt.perplexityChatCompletionRequest.messages.${number}.content`
      | `materializedPrompt.perplexityChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.perplexityChatCompletionRequest.searchDomainFilter`
      | `materializedPrompt.perplexityChatCompletionRequest.responseFormat.jsonSchema`
      | `materializedPrompt.perplexityChatCompletionRequest.responseFormat.regex`
      | `materializedPrompt.googleGenerateImageRequest.model`
      | `materializedPrompt.googleGenerateImageRequest.instances`
      | `materializedPrompt.mlPlatformGenerateImageRequest.model`
      | `materializedPrompt.openAiCreateOpenAiImageRequest.model`
      | `materializedPrompt.openAiEditOpenAiImageRequest.model`
      | `materializedPrompt.openAiEditOpenAiImageRequest.imageUrls`
      | `materializedPrompt.googleGenerateVideoRequest.model`
      | `materializedPrompt.googleGenerateVideoRequest.instances`
      | `materializedPrompt.googleCreateChatCompletionRequest.model`
      | `materializedPrompt.googleCreateChatCompletionRequest.messages`
      | `materializedPrompt.googleCreateChatCompletionRequest.messages.${number}.role`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.modelId`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.messages`
      | `materializedPrompt.mlPlatformOpenAiRawRequest.messages.${number}.role`
      | `materializedPrompt.runwareVideoInferenceRequest.outputFormat`
      | `materializedPrompt.runwareVideoInferenceRequest.frameImages`
      | `materializedPrompt.runwareVideoInferenceRequest.frameImages.${number}.inputImage`
      | `materializedPrompt.runwareVideoInferenceRequest.referenceImages`
      | `materializedPrompt.runwareVideoInferenceRequest.model`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.frameImages`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.referenceImages`
      | `materializedPrompt.runwareVideoInferenceRequest.inputs.referenceVideos`
      | `materializedPrompt.openAiResponsesRequest.model`
      | `materializedPrompt.openAiResponsesRequest.include`
      | `materializedPrompt.openAiResponsesRequest.input`
      | `materializedPrompt.openAiResponsesRequest.input.${number}.message.role`
      | `materializedPrompt.openAiResponsesRequest.tools`
      | `materializedPrompt.azureOpenAiResponsesRequest.model`
      | `materializedPrompt.azureOpenAiResponsesRequest.include`
      | `materializedPrompt.azureOpenAiResponsesRequest.input`
      | `materializedPrompt.azureOpenAiResponsesRequest.input.${number}.message.role`
      | `materializedPrompt.azureOpenAiResponsesRequest.tools`
      | `materializedPrompt.openAiCreateVideoRequest.prompt`
      | `materializedPrompt.openAiCreateVideoRequest.model`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.modelId`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.messages`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.messages.${number}.role`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.functions`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.stop`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.tools`
      | `materializedPrompt.fireworksInvokeCustomOpenAiRequest.tools.${number}.type`
      | `materializedPrompt.bytedanceCreateContentTaskRequest.model`
      | `materializedPrompt.bytedanceCreateContentTaskRequest.content`
      | `materializedPrompt.bytedanceGenerateImageRequest.model`
      | `materializedPrompt.bytedanceGenerateImageRequest.prompt`
      | `materializedPrompt.bytedanceGenerateImageRequest.image`
      | `materializedPrompt.templatedParameterNames`
      | `materializedPrompt.templatedDynamicPropertiesNames`
      | `predictionId`,
      10
    >
  >;
}

/** @internal */
export function generateAudioStreamed(
  httpClient: HttpClient
): GenerateAudioStreamedSignature {
  return (options?: GenerateAudioStreamedOptions) =>
    universalGenerateAudioStreamed(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateAudioStreamedSignature {
  /** */
  (options?: GenerateAudioStreamedOptions): Promise<
    NonNullablePaths<
      GeneratedAudioChunk,
      | `openAiSpeechChunk.content`
      | `elevenlabsSpeechChunk.audioBase64`
      | `elevenlabsSpeechChunk.alignment.characterStartTimesSeconds`
      | `elevenlabsSpeechChunk.alignment.characterEndTimesSeconds`
      | `elevenlabsSpeechChunk.alignment.characters`,
      4
    >
  >;
}

export const onPromptProxyCompleted = EventDefinition(
  'wix.api_infra.v1.prompt_proxy_completed',
  true,
  (event: PromptProxyCompletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.generationResult.googleGeminiGenerateContentResponse.candidates.safetyRatings.probabilityScore',
            },
            {
              path: 'data.generationResult.googleGeminiGenerateContentResponse.candidates.safetyRatings.severityScore',
            },
            {
              path: 'data.generationResult.googleGenerateImageResponse.predictions.safetyAttributes.scores',
              isRepeated: true,
            },
            {
              path: 'data.generationResult.openAiResponsesResponse.temperature',
            },
            {
              path: 'data.generationResult.azureOpenAiResponsesResponse.temperature',
            },
            {
              path: 'data.generationResult.googleGeminiGenerateContentResponse.candidates.groundingMetadata.groundingSupports.confidenceScores',
              isRepeated: true,
            },
            {
              path: 'data.generationResult.googleGeminiGenerateContentResponse.candidates.groundingMetadata.retrievalMetadata.googleSearchDynamicRetrievalScore',
            },
          ],
        },
        {
          transformFn: transformRESTBytesToSDKBytes,
          paths: [
            {
              path: 'data.generationResult.googleGeminiGenerateContentResponse.candidates.groundingMetadata.searchEntryPoint.sdkBlob',
            },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<PromptProxyCompletedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  Action,
  ActionEvent,
  AlignmentInfoInChunk,
  AnthropicClaudeMessage,
  AnthropicMessage,
  AnthropicModel,
  AnthropicStreamChunk,
  AnthropicStreamChunkContentOneOf,
  AnthropicStreamChunkMessageDelta,
  AnthropicV1ImageUrl,
  AsyncGenerationConfig,
  BaseEventMetadata,
  BashTool,
  Blob,
  BuiltInTool,
  CacheControl,
  CacheControlType,
  Candidate,
  CandidateCitationMetadata,
  CandidateCitationMetadataCitation,
  CandidateContent,
  CandidateContentPart,
  CharLocationCitation,
  ChatCompletionChunk,
  ChatCompletionChunkChunkChoice,
  ChatCompletionChunkChunkChoiceChunkDelta,
  ChatCompletionMessage,
  ChatCompletionMessageContentPart,
  ChatCompletionMessageContentPartContentValueOneOf,
  ChatCompletionMessageFunctionWithArgs,
  ChatCompletionMessageImageUrlContent,
  ChatCompletionMessageMessageRole,
  ChatCompletionMessageToolCall,
  ChatCompletionModel,
  Choice,
  ChunkChoice,
  ChunkChoiceChunkDelta,
  ChunkDelta,
  Citation,
  CitationTypeOneOf,
  CitationsEnabled,
  ClaudeModel,
  ClipGuidancePreset,
  CodeExecution,
  CodeExecutionResult,
  CodeExecutionTool,
  CodeExecutionToolResult,
  CodeExecutionToolResultContentOneOf,
  CodeExecutionToolResultError,
  CommonContentTaskResponse,
  CompletionTokenDetails,
  ComputerUse,
  ComputerUseTool,
  Container,
  ContainerUpload,
  Content,
  ContentBlock,
  ContentBlockDelta,
  ContentBlockDeltaDeltaOneOf,
  ContentBlockLocationCitation,
  ContentBlockTypeOneOf,
  ContentData,
  ContentItem,
  ContentPart,
  ContentPartContentValueOneOf,
  ContentRole,
  ConverseContentBlock,
  ConverseContentBlockContentOneOf,
  ConverseInferenceConfig,
  ConverseInputSchema,
  ConverseMessage,
  ConverseModel,
  ConversePerformanceConfig,
  ConverseReasoningContent,
  ConverseTool,
  ConverseToolResult,
  ConverseToolResultContent,
  ConverseToolResultContentContentOneOf,
  ConverseToolUse,
  CreateChatCompletionRequest,
  CreateChatCompletionRequestFunctionCallOneOf,
  CreateChatCompletionRequestFunctionSignature,
  CreateChatCompletionRequestResponseFormat,
  CreateChatCompletionRequestTool,
  CreateChatCompletionResponse,
  CreateChatCompletionResponseChoice,
  CreateChatCompletionResponseCompletionTokenDetails,
  CreateChatCompletionResponsePromptTokenDetails,
  CreateChatCompletionResponseTokenUsage,
  CreateContentTaskRequest,
  CreateContentTaskResponse,
  CreateImageOpenAiRequest,
  CreateImageOpenAiResponse,
  CreateImageRequest,
  CreateImageResponse,
  CreatePredictionModel,
  CreatePredictionRequest,
  CreatePredictionRequestInputOneOf,
  CreatePredictionResponse,
  CreatePredictionResponseTokenUsage,
  CreateSpeechRequest,
  CreateVideoRequest,
  CreateVideoResponse,
  CustomTool,
  DocumentContent,
  DocumentSource,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftTask,
  DynamicRequestConfig,
  DynamicRetrievalConfig,
  DynamicRetrievalConfigMode,
  EditImageOpenAiRequest,
  EditImageOpenAiResponse,
  EditImageWithPromptRequest,
  EditImageWithPromptRequestModel,
  EditImageWithPromptResponse,
  ElevenLabsTextToSpeechModel,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Environment,
  ErrorInfo,
  EventMetadata,
  ExecutableCode,
  FallbackPromptConfig,
  FallbackProperties,
  FileInput,
  FineTuningSpec,
  FinishReason,
  Fireworks_proxyV1ChatCompletionMessage,
  Fireworks_proxyV1ChatCompletionMessageContentPart,
  Fireworks_proxyV1ChatCompletionMessageContentPartContentValueOneOf,
  Fireworks_proxyV1ChatCompletionMessageImageUrlContent,
  Fireworks_proxyV1ChatCompletionMessageMessageRole,
  FluxDevControlnet,
  FluxPulid,
  FrameImage,
  FunctionCall,
  FunctionCallingConfig,
  FunctionDeclaration,
  FunctionResponse,
  FunctionResponseBlob,
  FunctionResponsePart,
  FunctionResponsePartDataOneOf,
  FunctionSignature,
  FunctionWithArgs,
  GatewayContentBlock,
  GatewayContentBlockTypeOneOf,
  GatewayMessageDefinition,
  GatewayMessageDefinitionRole,
  GatewayToolDefinition,
  GatewayToolDefinitionCustomTool,
  GatewayToolDefinitionToolOneOf,
  GenerateAnImageModel,
  GenerateAnImageRequest,
  GenerateAnImageResponse,
  GenerateAudioRequest,
  GenerateAudioRequestAudioRequestOneOf,
  GenerateAudioStreamedOptions,
  GenerateAudioStreamedOptionsAudioRequestOneOf,
  GenerateContentByPromptObjectAsyncOptions,
  GenerateContentByPromptObjectOptions,
  GenerateContentByPromptObjectRequest,
  GenerateContentByPromptObjectResponse,
  GenerateContentModelResponse,
  GenerateContentModelResponseResponseOneOf,
  GenerateContentRequest,
  GenerateContentResponse,
  GenerateCoreRequest,
  GenerateCoreRequestStylePreset,
  GenerateCoreResponse,
  GenerateImageMlPlatformModel,
  GenerateImageMlPlatformRequest,
  GenerateImageMlPlatformRequestInputOneOf,
  GenerateImageMlPlatformResponse,
  GenerateImageRequest,
  GenerateImageResponse,
  GenerateStableDiffusionRequest,
  GenerateStableDiffusionRequestOutputFormat,
  GenerateStableDiffusionResponse,
  GenerateTextByPromptObjectRequest,
  GenerateTextByPromptObjectStreamedOptions,
  GenerateVideoInstance,
  GenerateVideoParameters,
  GenerateVideoRequest,
  GenerateVideoResponse,
  GeneratedAudioChunk,
  GeneratedAudioChunkAudioChunkOneOf,
  GeneratedContent,
  GeneratedTextChunk,
  GeneratedTextChunkModelChunkOneOf,
  GeneratedVideo,
  GenerationCompletedResultEvent,
  GenerationConfig,
  GenerationMode,
  GenerationThinkingConfig,
  GoogleSearch,
  GoogleSearchRetrieval,
  GoogleproxyV1AnthropicStreamChunk,
  GoogleproxyV1AnthropicStreamChunkContentOneOf,
  GoogleproxyV1CacheControl,
  GoogleproxyV1ChatCompletionMessage,
  GoogleproxyV1ContentBlock,
  GoogleproxyV1ContentBlockDelta,
  GoogleproxyV1ContentBlockDeltaDeltaOneOf,
  GoogleproxyV1ContentBlockTypeOneOf,
  GoogleproxyV1ImageUrl,
  GoogleproxyV1InputSchema,
  GoogleproxyV1McpServer,
  GoogleproxyV1McpServerType,
  GoogleproxyV1Model,
  GoogleproxyV1RedactedThinking,
  GoogleproxyV1ResponseTypeType,
  GoogleproxyV1Text,
  GoogleproxyV1Thinking,
  GoogleproxyV1ThinkingConfig,
  GoogleproxyV1Tool,
  GoogleproxyV1ToolChoice,
  GoogleproxyV1ToolChoiceType,
  GoogleproxyV1ToolResult,
  GoogleproxyV1ToolUse,
  GoogleproxyV1Usage,
  GroundingChunk,
  GroundingChunkChunkTypeOneOf,
  GroundingMetadata,
  GroundingSupport,
  HarmCategory,
  HarmProbability,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImageConfig,
  ImageCoreModel,
  ImageData,
  ImageError,
  ImageInput,
  ImageMediaTypeMediaType,
  ImageModel,
  ImageObject,
  ImageOutputOptions,
  ImageQuality,
  ImageSize,
  ImageStableDiffusionModel,
  ImageStyle,
  ImageUrl,
  ImageUrlContent,
  ImageUsage,
  ImagenModel,
  IncompleteDetails,
  InputSchema,
  Inputs,
  Instance,
  InvokeAnthropicClaudeModelRequest,
  InvokeAnthropicClaudeModelRequestTool,
  InvokeAnthropicClaudeModelResponse,
  InvokeAnthropicModelRequest,
  InvokeAnthropicModelResponse,
  InvokeChatCompletionRequest,
  InvokeChatCompletionRequestResponseFormat,
  InvokeChatCompletionRequestResponseFormatFormatDetailsOneOf,
  InvokeChatCompletionResponse,
  InvokeChatCompletionResponseChoice,
  InvokeChatCompletionResponseUsage,
  InvokeConverseRequest,
  InvokeConverseResponse,
  InvokeConverseResponseTokenUsage,
  InvokeCustomOpenAiModelRequest,
  InvokeCustomOpenAiModelRequestFunctionCallOneOf,
  InvokeCustomOpenAiModelRequestFunctionSignature,
  InvokeCustomOpenAiModelRequestResponseFormat,
  InvokeCustomOpenAiModelRequestTool,
  InvokeCustomOpenAiModelResponse,
  InvokeCustomOpenAiModelResponseChoice,
  InvokeCustomOpenAiModelResponseCompletionTokenDetails,
  InvokeCustomOpenAiModelResponsePromptTokenDetails,
  InvokeCustomOpenAiModelResponseTokenUsage,
  InvokeLlamaModelRequest,
  InvokeLlamaModelResponse,
  InvokeMlPlatformLlamaModelRequest,
  InvokeMlPlatformLlamaModelResponse,
  InvokeMlPlatformOpenAIChatCompletionRawRequest,
  InvokeMlPlatformOpenAIChatCompletionRawResponse,
  JsonSchema,
  Language,
  LlamaModel,
  LoraModelSelect,
  LucatacoFlorence2Large,
  McpServer,
  McpServerToolConfiguration,
  McpServerType,
  McpToolUse,
  MediaContent,
  MediaResolution,
  MediaResolutionLevel,
  MediaType,
  MessageDelta,
  MessageEnvelope,
  MessageRole,
  MessageRoleRole,
  Metrics,
  Modality,
  ModalityTokenCount,
  Mode,
  Model,
  OpenAiImageModel,
  OpenAiImageTokenDetails,
  OpenAiResponsesRequest,
  OpenAiResponsesResponse,
  OpenAiResponsesResponseIncompleteDetails,
  OpenaiproxyV1ChatCompletionChunk,
  OpenaiproxyV1ChatCompletionMessage,
  OpenaiproxyV1ChatCompletionMessageContentPart,
  OpenaiproxyV1ChatCompletionMessageContentPartContentValueOneOf,
  OpenaiproxyV1ChatCompletionMessageImageUrlContent,
  OpenaiproxyV1ChatCompletionMessageMessageRole,
  OpenaiproxyV1CreateChatCompletionRequest,
  OpenaiproxyV1CreateChatCompletionRequestFunctionCallOneOf,
  OpenaiproxyV1CreateChatCompletionRequestResponseFormat,
  OpenaiproxyV1CreateChatCompletionResponse,
  OpenaiproxyV1CreateChatCompletionResponseChoice,
  OpenaiproxyV1CreateChatCompletionResponseTokenUsage,
  OpenaiproxyV1Model,
  OptimizePromptOptions,
  Outcome,
  OutpaintDirection,
  Output,
  OutputAnnotation,
  OutputAnnotationAnnotationTypeOneOf,
  OutputContent,
  OutputFormat,
  OutputOptions,
  PageLocationCitation,
  Parameters,
  PerceptronIsaac01,
  PerplexityImageDescriptor,
  PerplexityMessage,
  PerplexityMessageMessageRole,
  PerplexityModel,
  PersonGeneration,
  Prediction,
  Prompt,
  PromptModelRequestOneOf,
  PromptProxy,
  PromptProxyCompletedEnvelope,
  PromptTokenDetails,
  PronunciationDictionaryLocator,
  PrunaaiZImageTurbo,
  PublicationDate,
  QwenImageLayered,
  ReasoningText,
  RedactedThinking,
  RequestMetadata,
  ResponseFormat,
  ResponseMetadata,
  ResponseType,
  ResponseTypeType,
  ResponsesCodeInterpreter,
  ResponsesCodeInterpreterContainer,
  ResponsesCodeInterpreterContainerAuto,
  ResponsesCodeInterpreterContainerContainerTypeOneOf,
  ResponsesCodeInterpreterImageOutput,
  ResponsesCodeInterpreterLogsOutput,
  ResponsesCodeInterpreterOutput,
  ResponsesCodeInterpreterOutputOutputTypeOneOf,
  ResponsesCodeInterpreterToolCall,
  ResponsesFunction,
  ResponsesFunctionToolCall,
  ResponsesFunctionToolCallOutput,
  ResponsesInputItem,
  ResponsesInputItemItemOneOf,
  ResponsesInputMessage,
  ResponsesInputMessageContent,
  ResponsesInputMessageContentContentValueOneOf,
  ResponsesInputMessageContentFileInput,
  ResponsesInputMessageContentImageInput,
  ResponsesInputMessageResponsesMessageRole,
  ResponsesInputTokensDetails,
  ResponsesMessageRole,
  ResponsesModel,
  ResponsesOutput,
  ResponsesOutputMessage,
  ResponsesOutputMessageOutputContent,
  ResponsesOutputOutputOneOf,
  ResponsesOutputTokensDetails,
  ResponsesReasoning,
  ResponsesReasoningContent,
  ResponsesReasoningOutput,
  ResponsesReasoningSummaryContent,
  ResponsesTextFormat,
  ResponsesTextFormatFormatOneOf,
  ResponsesTextFormatJsonSchema,
  ResponsesTokenUsage,
  ResponsesTool,
  ResponsesToolChoice,
  ResponsesToolToolTypeOneOf,
  ResponsesWebSearch,
  ResponsesWebSearchToolCall,
  ResponsesWebSearchToolCallAction,
  ResponsesWebSearchUserLocation,
  RestoreInfo,
  ResultObject,
  RetrievalMetadata,
  RetrievedContext,
  ReveEdit,
  Role,
  SafetyAttributes,
  SafetyRating,
  SafetySetting,
  Sampler,
  SearchEntryPoint,
  SearchResultLocationCitation,
  Segment,
  SequentialImageGenerationOptions,
  ServerToolUse,
  SimpleContentBlock,
  SimpleContentBlockTypeOneOf,
  SpeechChunk,
  SpeechModel,
  SpiGenerationConfig,
  StylePreset,
  SystemContentBlock,
  SystemInstruction,
  TaskContent,
  TaskError,
  TaskInput,
  TaskUsage,
  Text,
  TextContent,
  TextEditorTool,
  TextPrompt,
  TextToImageRequest,
  TextToImageRequestModel,
  TextToImageRequestStylePreset,
  TextToImageResponse,
  TextToImageTaskResult,
  TextToSpeechChunk,
  TextToSpeechRequest,
  Thinking,
  ThinkingConfig,
  ThinkingTextContent,
  Threshold,
  TokenUsage,
  Tool,
  ToolCall,
  ToolChoice,
  ToolChoiceType,
  ToolConfig,
  ToolConfiguration,
  ToolResult,
  ToolResultContent,
  ToolResultContentBlock,
  ToolResultContentBlockTypeOneOf,
  ToolResultSearchResult,
  ToolSpecification,
  ToolUse,
  ToolUseContent,
  Type,
  UrlCitation,
  Usage,
  UsageCacheCreation,
  UsageMetadata,
  UsageServerToolUse,
  UserLocation,
  UserRequestInfo,
  V1AnthropicClaudeMessage,
  V1AnthropicStreamChunk,
  V1AnthropicStreamChunkContentOneOf,
  V1AnthropicStreamChunkMessageDelta,
  V1CacheControl,
  V1CacheControlType,
  V1ChatCompletionChunk,
  V1ChatCompletionChunkChunkChoice,
  V1ChatCompletionMessage,
  V1ChatCompletionMessageContentPart,
  V1ChatCompletionMessageContentPartContentValueOneOf,
  V1ChatCompletionMessageFunctionWithArgs,
  V1ChatCompletionMessageImageUrlContent,
  V1ChatCompletionMessageMessageRole,
  V1ChatCompletionMessageToolCall,
  V1CodeExecutionResult,
  V1ContentBlock,
  V1ContentBlockDelta,
  V1ContentBlockDeltaDeltaOneOf,
  V1ContentBlockTypeOneOf,
  V1ContentPart,
  V1CreateChatCompletionRequest,
  V1CreateChatCompletionRequestResponseFormat,
  V1CreateChatCompletionRequestTool,
  V1CreateChatCompletionResponse,
  V1CreateChatCompletionResponseChoice,
  V1CreateChatCompletionResponseTokenUsage,
  V1FineTuningSpec,
  V1FluxPulid,
  V1GenerateImageRequest,
  V1GenerateImageResponse,
  V1ImageInput,
  V1ImageMediaTypeMediaType,
  V1ImageModel,
  V1ImageObject,
  V1ImageUrl,
  V1ImageUsage,
  V1InputSchema,
  V1InvokeAnthropicClaudeModelRequest,
  V1InvokeAnthropicClaudeModelResponse,
  V1McpServer,
  V1McpServerToolConfiguration,
  V1McpServerType,
  V1MessageRoleRole,
  V1Model,
  V1OpenAiResponsesRequest,
  V1OpenAiResponsesResponse,
  V1OutputAnnotation,
  V1OutputAnnotationAnnotationTypeOneOf,
  V1RedactedThinking,
  V1ResponseTypeType,
  V1ResponsesCodeInterpreter,
  V1ResponsesCodeInterpreterContainer,
  V1ResponsesCodeInterpreterContainerAuto,
  V1ResponsesCodeInterpreterContainerContainerTypeOneOf,
  V1ResponsesCodeInterpreterImageOutput,
  V1ResponsesCodeInterpreterLogsOutput,
  V1ResponsesCodeInterpreterOutput,
  V1ResponsesCodeInterpreterOutputOutputTypeOneOf,
  V1ResponsesCodeInterpreterToolCall,
  V1ResponsesFunction,
  V1ResponsesFunctionToolCall,
  V1ResponsesFunctionToolCallOutput,
  V1ResponsesInputItem,
  V1ResponsesInputItemItemOneOf,
  V1ResponsesInputMessage,
  V1ResponsesInputMessageContent,
  V1ResponsesInputMessageContentContentValueOneOf,
  V1ResponsesInputTokensDetails,
  V1ResponsesModel,
  V1ResponsesOutput,
  V1ResponsesOutputMessage,
  V1ResponsesOutputOutputOneOf,
  V1ResponsesOutputTokensDetails,
  V1ResponsesReasoning,
  V1ResponsesReasoningContent,
  V1ResponsesReasoningOutput,
  V1ResponsesReasoningSummaryContent,
  V1ResponsesTextFormat,
  V1ResponsesTextFormatFormatOneOf,
  V1ResponsesTokenUsage,
  V1ResponsesTool,
  V1ResponsesToolChoice,
  V1ResponsesToolToolTypeOneOf,
  V1ResponsesWebSearch,
  V1ResponsesWebSearchToolCall,
  V1SimpleContentBlock,
  V1SimpleContentBlockTypeOneOf,
  V1Text,
  V1TextToImageRequest,
  V1TextToImageResponse,
  V1Thinking,
  V1ThinkingConfig,
  V1TokenUsage,
  V1Tool,
  V1ToolChoice,
  V1ToolChoiceType,
  V1ToolConfig,
  V1ToolKindOneOf,
  V1ToolResult,
  V1ToolUse,
  V1UrlCitation,
  V1Usage,
  V1VideoModel,
  VideoGenModel,
  VideoInferenceRequest,
  VideoInferenceResponse,
  VideoInferenceTaskResult,
  VideoInputs,
  VideoJob,
  VideoModel,
  VoiceSettings,
  Web,
  WebFetchTool,
  WebFetchToolResult,
  WebFetchToolResultContentError,
  WebFetchToolResultContentOneOf,
  WebFetchToolResultContentSuccess,
  WebSearchResult,
  WebSearchResultList,
  WebSearchResultLocationCitation,
  WebSearchTool,
  WebSearchToolResult,
  WebSearchToolResultContentOneOf,
  WebSearchToolResultError,
  WebSearchUserLocation,
  WebhookIdentityType,
} from './api-infra-v1-prompt-proxy-prompts.universal.js';
